export function simplifyFraction(numerator, denominator = 1) {
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator)) {
    throw new TypeError("Fraction numerator and denominator must be integers.");
  }
  if (denominator === 0) {
    throw new RangeError("Fraction denominator cannot be zero.");
  }

  let n = numerator;
  let d = denominator;
  if (d < 0) {
    n = -n;
    d = -d;
  }

  let a = Math.abs(n);
  let b = d;
  while (b) {
    [a, b] = [b, a % b];
  }
  const divisor = a || 1;

  return { num: n / divisor, den: d / divisor };
}

const FRACTION_COMMAND_BY_STYLE = Object.freeze({
  display: "dfrac",
  inline: "frac",
  text: "tfrac",
});

export function formatFractionLatex(
  numerator,
  denominator = 1,
  { style = "display" } = {}
) {
  const { num, den } = simplifyFraction(numerator, denominator);
  if (den === 1) return String(num);
  const command = FRACTION_COMMAND_BY_STYLE[style];
  if (!command) {
    throw new RangeError(`Unknown fraction LaTeX style: ${style}`);
  }

  const sign = num < 0 ? "-" : "";
  return `${sign}\\${command}{${Math.abs(num)}}{${den}}`;
}

export function formatMixedFractionLatex(numerator, denominator = 1) {
  const { num, den } = simplifyFraction(numerator, denominator);
  if (den === 1) return String(num);

  const sign = num < 0 ? "-" : "";
  const absoluteNumerator = Math.abs(num);
  const whole = Math.floor(absoluteNumerator / den);
  const remainder = absoluteNumerator % den;

  if (whole === 0) return `${sign}\\dfrac{${remainder}}{${den}}`;
  if (remainder === 0) return `${sign}${whole}`;
  return `${sign}${whole}\\dfrac{${remainder}}{${den}}`;
}

export function approximateFraction(
  value,
  { maxDenominator = 20, tolerance = 0.01 } = {}
) {
  if (!Number.isFinite(value)) return null;
  if (!Number.isInteger(maxDenominator) || maxDenominator < 1) {
    throw new RangeError("maxDenominator must be a positive integer.");
  }

  let best = null;
  for (let denominator = 1; denominator <= maxDenominator; denominator += 1) {
    const numerator = Math.round(value * denominator);
    const error = Math.abs(numerator / denominator - value);
    if (!best || error < best.error) {
      best = { numerator, denominator, error };
    }
  }

  if (!best || best.error > tolerance) return null;
  return simplifyFraction(best.numerator, best.denominator);
}

export function formatApproximateMixedFractionLatex(
  value,
  {
    maxDenominator = 20,
    tolerance = 0.01,
    fallbackDigits = 2,
  } = {}
) {
  if (!Number.isFinite(value)) return String(value);

  const fraction = approximateFraction(value, {
    maxDenominator,
    tolerance,
  });
  if (fraction) {
    return formatMixedFractionLatex(fraction.num, fraction.den);
  }

  return Number(value.toFixed(fallbackDigits)).toString();
}

export function toTextFractionLatex(latex) {
  return String(latex).replace(/\\(?:d)?frac/g, "\\tfrac");
}
